package com.advaita.cloud.drive.ui.api

import androidx.compose.runtime.NoLiveLiterals
import com.advaita.cloud.drive.shared.FileIdentity
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlinx.serialization.Serializable
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException
import kotlin.js.Date
import kotlin.js.Promise
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.khronos.webgl.Uint8Array
import org.khronos.webgl.get

/**
 * Durable, per-user store for resumable-upload metadata, backed by IndexedDB and
 * encrypted at rest with WebCrypto (AES-GCM). Only *metadata* is ever persisted
 * (filename, paths, mime, part geometry) — never file bytes. The server (via
 * [FilesApi.getUploadStatus]) remains the source of truth for which parts remain;
 * this store never tracks part progress.
 *
 * The sensitive fields (filename + paths) are encrypted with a server-provided
 * per-user key (see [FilesApi.getResumeKey]) so that a *different* machine-user
 * sharing the same browser profile can't read another user's pending filenames.
 * [ownerKeyHash] (a SHA-256 of "orgId:username") is a non-sensitive filter/prune
 * key used to scope reads to the current user and to garbage-collect foreign
 * records.
 */
@NoLiveLiterals
class ResumableUploadStore {

    private var orgId: String = ""
    private var username: String = ""

    /** Non-extractable AES-GCM CryptoKey imported in [init]. Opaque; kept dynamic. */
    private var cryptoKey: dynamic = null

    /** The open IDBDatabase handle. Opaque; kept dynamic. */
    private var db: dynamic = null

    /** SHA-256("orgId:username") in lowercase hex — computed once in [init]. */
    private var myOwnerKeyHash: String = ""


    /**
     * Import the per-user key and open the database. Must be called (and awaited)
     * before any other method.
     *
     * @param keyBase64 base64 of the raw 32-byte AES key from the server.
     * @param orgId caller's org id (part of the owner-scoping hash).
     * @param username caller's username (part of the owner-scoping hash).
     */
    suspend fun init(keyBase64: String, orgId: String, username: String) {
        this.orgId = orgId
        this.username = username
        this.myOwnerKeyHash = sha256Hex("$orgId:$username")

        val keyBytes = base64ToBytes(keyBase64)
        val algo = jsObject()
        algo.name = "AES-GCM"
        cryptoKey = (subtle.importKey(
            "raw",
            keyBytes,
            algo,
            false, // non-extractable
            arrayOf("encrypt", "decrypt"),
        ) as Promise<dynamic>).await()

        db = openDatabase()

        // Best-effort: ask the browser to keep this storage across eviction
        // pressure. Ignore the result (and any absence of the API).
        runCatching {
            js("navigator.storage && navigator.storage.persist && navigator.storage.persist()")
        }
    }

    /** Encrypt and persist (put) one record. Overwrites any record with the same sessionId. */
    suspend fun put(record: PlainRecord) {
        val plainBytes = encodeSensitive(sensitiveOf(record)).encodeToByteArray()

        val iv = Uint8Array(GCM_IV_BYTES)
        crypto.getRandomValues(iv)

        val algo = jsObject()
        algo.name = "AES-GCM"
        algo.iv = iv
        val ctBuf = (subtle.encrypt(algo, cryptoKey, plainBytes) as Promise<ArrayBuffer>).await()

        val row = jsObject()
        row.sessionId = record.sessionId
        row.ownerKeyHash = myOwnerKeyHash
        row.iv = bytesToBase64(uint8ToByteArray(iv))
        row.ct = bytesToBase64(arrayBufferToByteArray(ctBuf))
        row.partSize = record.partSize.toDouble()   // Long -> JS number for structured clone
        row.totalParts = record.totalParts
        row.createdAt = record.createdAt

        putRow(row)
    }

    /**
     * Return the current user's still-valid records, decrypted.
     *
     * Records are dropped (and, for the age case, deleted) when they:
     *  - are older than 30 days (unconditional growth-safety prune, deleted from the store), or
     *  - belong to a different owner ([ownerKeyHash] mismatch), or
     *  - fail to decrypt (foreign key / rotated key / corruption).
     */
    suspend fun listMine(): List<PlainRecord> {
        val rows = getAllRows()
        val cutoff = Date.now() - THIRTY_DAYS_MS
        val out = mutableListOf<PlainRecord>()
        val len = (rows.length as Int)
        for (i in 0 until len) {
            val row = rows[i]
            val sessionId = row.sessionId as String
            val createdAt = (row.createdAt as? Number)?.toDouble() ?: 0.0

            if (createdAt < cutoff) {
                runCatching { deleteRow(sessionId) }
                continue
            }
            if ((row.ownerKeyHash as? String) != myOwnerKeyHash) continue

            val decrypted = runCatching {
                val iv = base64ToBytes(row.iv as String)
                val ct = base64ToBytes(row.ct as String)
                val algo = jsObject()
                algo.name = "AES-GCM"
                algo.iv = iv
                val ptBuf = (subtle.decrypt(algo, cryptoKey, ct) as Promise<ArrayBuffer>).await()
                decodeSensitive(arrayBufferToByteArray(ptBuf).decodeToString())
            }.getOrNull() ?: continue

            out.add(
                recordFromSensitive(
                    sessionId,
                    decrypted,
                    partSize = ((row.partSize as Number).toDouble()).toLong(),
                    totalParts = (row.totalParts as Number).toInt(),
                    createdAt = createdAt,
                ),
            )
        }
        return out
    }

    /** Delete one record by session id. */
    suspend fun delete(sessionId: String) {
        deleteRow(sessionId)
    }

    /** Clear the entire object store. Used on logout. */
    suspend fun clearAll() {
        val tx = db.transaction(arrayOf(STORE), "readwrite")
        tx.objectStore(STORE).clear()
        awaitTransaction(tx)
    }

    /** Delete every record that does NOT belong to the current owner. */
    suspend fun pruneForeign() {
        val rows = getAllRows()
        val len = (rows.length as Int)
        for (i in 0 until len) {
            val row = rows[i]
            if ((row.ownerKeyHash as? String) != myOwnerKeyHash) {
                runCatching { deleteRow(row.sessionId as String) }
            }
        }
    }

    // ---- IndexedDB plumbing ------------------------------------------------

    private suspend fun openDatabase(): dynamic = suspendCancellableCoroutine { cont ->
        val request = indexedDB.open(DB_NAME, DB_VERSION)
        request.onupgradeneeded = {
            val database = request.result
            if (!(database.objectStoreNames.contains(STORE) as Boolean)) {
                val opts = jsObject()
                opts.keyPath = "sessionId"
                database.createObjectStore(STORE, opts)
            }
        }
        request.onsuccess = { cont.resume(request.result) }
        request.onerror = {
            cont.resumeWithException(RuntimeException("IndexedDB open failed: ${request.error}"))
        }
        request.onblocked = {
            cont.resumeWithException(RuntimeException("IndexedDB open blocked (advaita-drive)"))
        }
    }

    /** getAll() from the object store — returns the raw JS array of stored rows. */
    private suspend fun getAllRows(): dynamic {
        val tx = db.transaction(arrayOf(STORE), "readonly")
        val request = tx.objectStore(STORE).getAll()
        return awaitRequest(request)
    }

    private suspend fun putRow(row: dynamic) {
        val tx = db.transaction(arrayOf(STORE), "readwrite")
        tx.objectStore(STORE).put(row)
        awaitTransaction(tx)
    }

    private suspend fun deleteRow(sessionId: String) {
        val tx = db.transaction(arrayOf(STORE), "readwrite")
        tx.objectStore(STORE).delete(sessionId)
        awaitTransaction(tx)
    }

    /** Resume with an IDBRequest's result on success. */
    private suspend fun awaitRequest(request: dynamic): dynamic = suspendCancellableCoroutine { cont ->
        request.onsuccess = { cont.resume(request.result) }
        request.onerror = {
            cont.resumeWithException(RuntimeException("IndexedDB request failed: ${request.error}"))
        }
    }

    /** Resume when a write transaction fully commits (durability), not just at request success. */
    private suspend fun awaitTransaction(tx: dynamic): Unit = suspendCancellableCoroutine { cont ->
        tx.oncomplete = { cont.resume(Unit) }
        tx.onerror = {
            cont.resumeWithException(RuntimeException("IndexedDB transaction failed: ${tx.error}"))
        }
        tx.onabort = { cont.resumeWithException(RuntimeException("IndexedDB transaction aborted")) }
    }

    // ---- WebCrypto helpers -------------------------------------------------

    private suspend fun sha256Hex(text: String): String {
        val digest = (subtle.digest("SHA-256", text.encodeToByteArray()) as Promise<ArrayBuffer>).await()
        val bytes = arrayBufferToByteArray(digest)
        val sb = StringBuilder(bytes.size * 2)
        for (b in bytes) {
            val v = b.toInt() and 0xFF
            sb.append(HEX[v ushr 4])
            sb.append(HEX[v and 0x0F])
        }
        return sb.toString()
    }

    // ---- base64 (binary-safe) ---------------------------------------------

    private fun bytesToBase64(bytes: ByteArray): String {
        val sb = StringBuilder(bytes.size)
        for (b in bytes) sb.append((b.toInt() and 0xFF).toChar())
        return window.asDynamic().btoa(sb.toString()) as String
    }

    private fun base64ToBytes(b64: String): ByteArray {
        val binary = window.asDynamic().atob(b64) as String
        return ByteArray(binary.length) { (binary[it].code and 0xFF).toByte() }
    }

    // ---- typed-array <-> ByteArray ----------------------------------------

    private fun arrayBufferToByteArray(buffer: ArrayBuffer): ByteArray {
        val view = Int8Array(buffer)
        return ByteArray(view.length) { view[it] }
    }

    private fun uint8ToByteArray(view: Uint8Array): ByteArray =
        ByteArray(view.length) { view[it] }

    // ---- JS interop accessors ---------------------------------------------

    private val crypto: dynamic get() = js("window.crypto")
    private val subtle: dynamic get() = js("window.crypto.subtle")
    private val indexedDB: dynamic get() = js("window.indexedDB")
    private fun jsObject(): dynamic = js("({})")

    companion object {
        private const val DB_NAME = "advaita-drive"
        private const val DB_VERSION = 1
        private const val STORE = "resumableUploads"
        private const val GCM_IV_BYTES = 12
        private const val THIRTY_DAYS_MS = 30.0 * 24.0 * 60.0 * 60.0 * 1000.0
        private val HEX = "0123456789abcdef".toCharArray()
    }
}

/**
 * The decrypted, in-memory view of a persisted resume record. Note [partSize]/
 * [totalParts] describe the multipart geometry only — actual remaining parts come
 * from the server ([FilesApi.getUploadStatus]).
 */
data class PlainRecord(
    val sessionId: String,
    val identity: com.advaita.cloud.drive.shared.FileIdentity,
    /** My Drive's folder path. Empty for a shared destination ([parentId] set). */
    val parentPath: String,
    val relativePath: String?,
    val mimeType: String?,
    val partSize: Long,
    val totalParts: Int,
    val createdAt: Double,   // epoch ms (Date.now())
    /**
     * ADV-1330 (the shared-folder create slice, Question 4 (a)): the id of the shared folder the
     * upload goes into, or null for My Drive. A record from before this field has none, and it
     * resumes by path, as it always did.
     */
    val parentId: String? = null,
    /** ADV-1330: the shared folder's name, for the resume panel. Null for My Drive. */
    val parentName: String? = null,
) {
    /** Where the upload goes, so a resume reloads that folder. */
    val destination: CreateTarget
        get() = parentId?.let { CreateTarget.SharedFolder(it, parentName.orEmpty()) } ?: CreateTarget.MyDrive(parentPath)

    /**
     * The destination as the resume panel shows it: My Drive's path, or "Shared with me ›" and
     * the shared folder's name. Example: "Shared with me › Run 42".
     */
    val destinationLabel: String
        get() = if (parentId != null) "Shared with me › ${parentName.orEmpty()}" else parentPath
}

/** The parent fields a resume record stores for one [CreateTarget]. */
internal data class RecordParent(val path: String, val id: String?, val name: String?)

internal fun CreateTarget.recordParent(): RecordParent = when (this) {
    is CreateTarget.MyDrive -> RecordParent(path, null, null)
    is CreateTarget.SharedFolder -> RecordParent("", folderId, name)
}

/**
 * The sensitive, encrypted-at-rest portion of a record. [parentId] and [parentName] are
 * optional, so a blob that an older build wrote (with no such keys) still decodes.
 */
@Serializable
internal data class SensitiveBlob(
    val identity: FileIdentity,
    val parentPath: String,
    val relativePath: String?,
    val mimeType: String?,
    val parentId: String? = null,
    val parentName: String? = null,
)

/** Old blobs carry keys this build may not know, and lack keys it added. */
private val sensitiveJson = Json { ignoreUnknownKeys = true }

internal fun sensitiveOf(record: PlainRecord) = SensitiveBlob(
    identity = record.identity,
    parentPath = record.parentPath,
    relativePath = record.relativePath,
    mimeType = record.mimeType,
    parentId = record.parentId,
    parentName = record.parentName,
)

internal fun encodeSensitive(blob: SensitiveBlob): String = sensitiveJson.encodeToString(blob)

internal fun decodeSensitive(text: String): SensitiveBlob = sensitiveJson.decodeFromString(text)

internal fun recordFromSensitive(
    sessionId: String,
    blob: SensitiveBlob,
    partSize: Long,
    totalParts: Int,
    createdAt: Double,
) = PlainRecord(
    sessionId = sessionId,
    identity = blob.identity,
    parentPath = blob.parentPath,
    relativePath = blob.relativePath,
    mimeType = blob.mimeType,
    partSize = partSize,
    totalParts = totalParts,
    createdAt = createdAt,
    parentId = blob.parentId,
    parentName = blob.parentName,
)
